import { useContext, useMemo } from "react";
import { Serie } from "@nivo/line";
import { useTranslation } from "react-i18next";
import { HMICarousel } from "../../../../components";
import { OEESidebarView } from "pages/HMI/HMIDashboard/components/OEEGaugesSidebar/OEEGaugesSidebar.view";
import { TrendSlide } from "./slide";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import {
    IOEECalculationSummary,
    IQualityOEECalculationSummary,
} from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";
import { applyPendingOverlay } from "utilities/qualityIntervals";

const VIEW_LABEL_KEYS: Record<string, string> = {
    oee: "HMI.oee_sidebar.oee",
    performance: "HMI.oee_sidebar.performance",
    quality: "HMI.oee_sidebar.quality",
};

interface OEECarouselProps {
    selectedView: OEESidebarView;
}

const ONE_HOUR_MS = 3_600_000;

/**
 * Formats a UTC timestamp as "HH:00" in the given timezone.
 */
function formatHourLabel(ms: number, timezone: string): string {
    const date = new Date(ms);
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(date);
    const hour = parts.find((p) => p.type === "hour")!.value;
    return `${hour}:00`;
}

/**
 * Builds Nivo chart data for the full shift hour grid.
 * Shows all hourly intervals from shift start to min(shift end, now),
 * with actual hour labels. Intervals without data are null (gaps in the line).
 */
function buildTrendData(
    records: IOEECalculationSummary[] | undefined,
    shiftStart: string | undefined,
    shiftEnd: string | undefined,
    timezone: string | undefined,
): Serie[] {
    if (!shiftStart || !shiftEnd || !timezone) return [];

    const now = new Date();
    const currentHourStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), 0, 0, 0),
    );
    const currentHourMs = currentHourStart.getTime();

    // Floor shift start to the hour boundary.
    const shiftStartMs = new Date(shiftStart).getTime();
    const shiftEndMs = new Date(shiftEnd).getTime();
    const gridStartMs = shiftStartMs - (shiftStartMs % ONE_HOUR_MS);
    const gridEndMs = Math.min(shiftEndMs, currentHourMs);

    if (gridStartMs >= gridEndMs) return [];

    // Build lookup map from records.
    // interval_start from the DB may lack a Z suffix, so ensure UTC parsing.
    const recordByInterval = new Map<number, IOEECalculationSummary>();
    if (records) {
        for (const r of records) {
            const raw = r.interval_start;
            const ms = new Date(raw.includes("T") ? raw : raw.replace(" ", "T") + "Z").getTime();
            if (ms < currentHourMs) {
                recordByInterval.set(ms, r);
            }
        }
    }

    // Walk every hour of the shift, using actual hour labels.
    const dataPoints: { x: string; y: number | null }[] = [];

    for (let ms = gridStartMs; ms < gridEndMs; ms += ONE_HOUR_MS) {
        const label = formatHourLabel(ms + ONE_HOUR_MS, timezone);
        const record = recordByInterval.get(ms);
        const value = record?.performance_ratio != null ? Math.round(record.performance_ratio * 100) : null;
        dataPoints.push({ x: label, y: value });
    }

    return [{ id: "trend", data: dataPoints }];
}

/**
 * Builds Nivo chart data for quality trend — same hourly grid as performance
 * but uses quality_ratio instead of performance_ratio.
 */
function buildQualityTrendData(
    records: IQualityOEECalculationSummary[] | undefined,
    shiftStart: string | undefined,
    shiftEnd: string | undefined,
    timezone: string | undefined,
): Serie[] {
    if (!shiftStart || !shiftEnd || !timezone) return [];

    const now = new Date();
    const currentHourStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), 0, 0, 0),
    );
    const currentHourMs = currentHourStart.getTime();

    const shiftStartMs = new Date(shiftStart).getTime();
    const shiftEndMs = new Date(shiftEnd).getTime();
    const gridStartMs = shiftStartMs - (shiftStartMs % ONE_HOUR_MS);
    const gridEndMs = Math.min(shiftEndMs, currentHourMs);

    if (gridStartMs >= gridEndMs) return [];

    const recordByInterval = new Map<number, IQualityOEECalculationSummary>();
    if (records) {
        for (const r of records) {
            const raw = r.interval_start;
            const ms = new Date(raw.includes("T") ? raw : raw.replace(" ", "T") + "Z").getTime();
            if (ms < currentHourMs) {
                recordByInterval.set(ms, r);
            }
        }
    }

    const dataPoints: { x: string; y: number | null }[] = [];

    for (let ms = gridStartMs; ms < gridEndMs; ms += ONE_HOUR_MS) {
        const label = formatHourLabel(ms + ONE_HOUR_MS, timezone);
        const record = recordByInterval.get(ms);
        const value = record?.quality_ratio != null ? Math.round(record.quality_ratio * 100) : null;
        dataPoints.push({ x: label, y: value });
    }

    return [{ id: "trend", data: dataPoints }];
}

export const OEETrendCarousel = ({ selectedView }: OEECarouselProps) => {
    const { t } = useTranslation();
    const { store } = useContext(HMICachedDataContext);
    const { shift } = useContext(HMIContext);

    const label = t(VIEW_LABEL_KEYS[selectedView] || "HMI.oee_sidebar.oee");
    const title = t("HMI.oee_trend.title", { label });

    const trendData = useMemo((): Serie[] => {
        if (selectedView !== OEESidebarView.PERFORMANCE && selectedView !== OEESidebarView.QUALITY) return [];

        const reports = store.lastTwoShiftsEventReports;
        const isCurrent = shift === ShiftOptionsEnum.current;

        const shiftStartVal = isCurrent ? reports?.currentShiftStart : reports?.previousShiftStart;
        const shiftEndVal = isCurrent ? reports?.currentShiftEnd : reports?.previousShiftEnd;

        if (selectedView === OEESidebarView.PERFORMANCE) {
            const summary = isCurrent
                ? reports?.currentShiftPerformanceSummary
                : reports?.previousShiftPerformanceSummary;
            return buildTrendData(summary, shiftStartVal, shiftEndVal, reports?.timezone);
        }

        // Silent pending overlay: trend chart shows operator-submitted values
        // immediately, before the next sync. No visual indicator on the chart;
        // the indicator lives in the modal.
        const baseQualitySummary = isCurrent
            ? reports?.currentShiftQualitySummary
            : reports?.previousShiftQualitySummary;
        const qualitySummary = applyPendingOverlay(
            baseQualitySummary,
            store.manualQualityPending,
            store.machineCenter?.machine_center_id,
            shiftStartVal,
            shiftEndVal,
        );
        return buildQualityTrendData(qualitySummary, shiftStartVal, shiftEndVal, reports?.timezone);
    }, [selectedView, shift, store.lastTwoShiftsEventReports, store.manualQualityPending, store.machineCenter?.machine_center_id]);

    return <HMICarousel slides={[<TrendSlide title={title} data={trendData} />]} />;
};
